// ts-rest contract for the Desk Dashboard (the IM's landing page).
//
// A per-view presentation projection of the IM's book: per-client AUM + YTD
// (from the ValuationGrid holdings, valued live via Yahoo) set against the ARC
// PCI benchmark for the client's risk band, plus an AUM-band segment estimate.
// Scoped server-side to the signed-in IM (Client.investmentManagerUserId); a
// firm-wide actor (ops / view / admin) sees the whole book. NOT a domain model;
// the controller maps ctx.read(...) + the shared valuation/benchmark engine
// onto these DTOs.
//
// Endpoint under /api/desk/*:
//   GET /api/desk/dashboard — book stats + per-client rows

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

/**
 * Performance window the dashboard's return figures are computed over.
 * `ytd` is the live per-client behaviour (per-ISIN Yahoo YTD); the calendar
 * windows + `custom` drive the firm-/benchmark-level series only — there is no
 * per-client historical valuation series, so per-client returns show "—" off-YTD
 * (ADR-0005: no fabricated returns). See the service for exactly what each
 * computes.
 */
export const deskPerformanceRangeSchema = z.enum(['1m', '6m', '1y', '3y', 'ytd', 'custom']);
export type DeskPerformanceRange = z.infer<typeof deskPerformanceRangeSchema>;

export const deskDashboardStatsSchema = z.object({
    clientCount: z.number().int(),
    /** AUM = Discretionary clients' closing value (the AUM/AUA split). */
    totalAumGbp: z.number(),
    /** AUA = Advisory + Execution-Only clients' closing value. */
    totalAuaGbp: z.number(),
    /** Whole-book closing value across every serviceType (AUM + AUA + unclassified). */
    grandTotalGbp: z.number(),
    /** Clients whose AUM-band estimate is Ultra High Net Worth. */
    uhnwCount: z.number().int(),
    /** AUM-weighted book YTD across priced holdings, or null if none priced. */
    avgYtdPct: z.number().nullable(),
    /** AUM-weighted ARC benchmark YTD for the book, or null if no benchmark. */
    avgBenchmarkYtdPct: z.number().nullable(),
    /** The ARC benchmark month-end the comparison is "as at", or null. */
    benchmarkAsOf: z.string().nullable(),
});
export type DeskDashboardStats = z.infer<typeof deskDashboardStatsSchema>;

/**
 * The benchmark's own return over the requested window, computed from the ARC
 * PCI level series (real where the levels span the window). Per-client returns
 * have no off-YTD source, so this firm-level benchmark figure is the only
 * range-aware return the dashboard can honestly show outside YTD.
 */
export const deskBenchmarkRangeSchema = z.object({
    /** The window actually computed (echoes the request; `custom` carries the dates). */
    range: deskPerformanceRangeSchema,
    /** AUM-weighted ARC benchmark return % over the window, or null if uncomputable. */
    benchmarkReturnPct: z.number().nullable(),
    /** The window's start month-end the return is measured from, or null. */
    fromAsOf: z.string().nullable(),
    /** The window's end month-end the return is measured to, or null. */
    toAsOf: z.string().nullable(),
});
export type DeskBenchmarkRange = z.infer<typeof deskBenchmarkRangeSchema>;

export const deskDashboardClientRowSchema = z.object({
    clientId: z.string(),
    fullName: z.string(),
    riskRating: z.string().nullable(),
    serviceType: z.string().nullable(),
    /** AUM-band estimate (never the canonical segment, which the grid leaves null). */
    segment: z.string().nullable(),
    /** True — the segment is an AUM-band estimate, for the UI to label. */
    segmentEstimated: z.boolean(),
    aumGbp: z.number().nullable(),
    ytdPct: z.number().nullable(),
    /** The ARC band this client is benchmarked against, e.g. "BalancedAsset". */
    arcBand: z.string().nullable(),
    benchmarkYtdPct: z.number().nullable(),
});
export type DeskDashboardClientRow = z.infer<typeof deskDashboardClientRowSchema>;

export const deskDashboardResponseSchema = z.object({
    stats: deskDashboardStatsSchema,
    clients: z.array(deskDashboardClientRowSchema),
    /** The IM the book is scoped to, or null for a firm-wide (ops/view/admin) read. */
    scopedToImUserId: z.string().nullable(),
    /** The benchmark's return over the requested performance window (range-aware). */
    benchmarkRange: deskBenchmarkRangeSchema,
});
export type DeskDashboardResponse = z.infer<typeof deskDashboardResponseSchema>;

export const deskDashboardContract = c.router(
    {
        getDashboard: {
            method: 'GET',
            path: '/desk/dashboard',
            query: z
                .object({
                    /** Performance window for the range-aware benchmark return. Defaults to YTD. */
                    range: deskPerformanceRangeSchema.optional(),
                    /** Custom window start (YYYY-MM-DD), used only when range is `custom`. */
                    from: z.string().optional(),
                    /** Custom window end (YYYY-MM-DD), used only when range is `custom`. */
                    to: z.string().optional(),
                })
                .optional(),
            responses: { 200: deskDashboardResponseSchema },
            summary: "The signed-in IM's book: AUM/AUA, YTD vs ARC benchmark, segment mix",
        },
    },
    {
        pathPrefix: '/api',
        strictStatusCodes: false,
    },
);
